import React, { useState, useContext } from 'react';
import {
  Box, Typography, Grid, Stack,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Loader } from '@alumni-ventures-group/react-shared/lib/components';
import PropTypes from 'prop-types';
import useStyles from '../styles';
import { PlanContext } from '../../context/PlanContext';
import PlanningButton from '../common/PlanningButton';
import {
  downloadFileById,
  getUserFiles,
} from '../../../../../shared/services/apiGateway';
import { selectLatestAvailableFile } from '../../helpers/selectLatestPPR';

export const PlanCompletionTypography = styled(Typography)(({ theme }) => ({
  fontSize: 20,
  fontWeight: 400,
  [theme.breakpoints.down('md')]: {
    fontSize: 16,
    fontWeight: 400,
  },
}));

const PostPlanSubmission = ({ onCreateNewPlan }) => {
  const { planningConfig, savePlan, userPortfolio } = useContext(PlanContext);
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [newPlanLoading, setNewPlanLoading] = useState(false);

  const handleDownloadPDF = async (event) => {
    event.preventDefault();
    setDownloadLoading(true);
    try {
      const vptReportResponse = await getUserFiles('', 'User_Investment_Plan');
      const latestReport = selectLatestAvailableFile(
        vptReportResponse,
        'User_Investment_Plan',
      );

      if (!latestReport) {
        throw new Error('No available investment plan report was found');
      }

      const fileDownloadUrl = await downloadFileById(latestReport.id);
      if (fileDownloadUrl === 'aborted' || !fileDownloadUrl?.fileUrl) {
        throw new Error('Investment plan report download did not return a URL');
      }

      window.open(fileDownloadUrl.fileUrl, '_blank');
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Failed to fetch user's investment plan:", error);
      }
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleCreateNewPlan = async () => {
    setNewPlanLoading(true);
    try {
      await savePlan({
        userId: parseInt(userPortfolio.integrationId, 10),
        createNewPlan: true,
        investmentPlanCompletionDate: '',
      });
      onCreateNewPlan();
    } catch (error) {
      console.error('Error creating new plan:', error);
    } finally {
      setNewPlanLoading(false);
    }
  };

  if (!planningConfig?.postPlanSubmission) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <Loader />
      </Box>
    );
  }

  return (
    <Grid container justifyContent="center" style={{ padding: '2rem' }}>
      <Grid item xs={12} md={8}>
        <Box textAlign="center">
          <Typography
            variant="h1ExtraLarge"
            component="h1"
            style={{ color: '#191919' }}
          >
            {planningConfig?.postPlanSubmission?.title}
          </Typography>
          <Box mt={2} mb={4}>
            <PlanCompletionTypography
              variant="body"
              dangerouslySetInnerHTML={{
                __html: planningConfig?.postPlanSubmission?.bodyCopy?.richtext,
              }}
            />
          </Box>
          <Box mb={4}>
            <Box
              component="button"
              type="button"
              onClick={handleDownloadPDF}
              sx={{
                padding: 0,
                border: 0,
                background: 'transparent',
                cursor: 'pointer',
              }}
            >
              <img
                src={planningConfig?.postPlanSubmission?.image?.url}
                alt="Download PDF"
                className={classes.image}
              />
            </Box>
          </Box>
          <Stack direction="column" alignItems="center" spacing={3}>
            <PlanningButton
              text={planningConfig?.postPlanSubmission?.downloadButtonCopy}
              onClick={handleDownloadPDF}
              isLoading={downloadLoading}
              disabled={downloadLoading}
              width={isMobile ? '260px' : '447px'}
            />
            <PlanningButton
              text={planningConfig?.postPlanSubmission?.createNewPlanButtonCopy}
              onClick={handleCreateNewPlan}
              isLoading={newPlanLoading}
              disabled={newPlanLoading}
              width={isMobile ? '260px' : '447px'}
            />
          </Stack>
          <Box mt={4}>
            <Typography
              variant="disclaimer"
              component="p"
              sx={{
                fontSize: '12px',
              }}
              dangerouslySetInnerHTML={{
                __html:
                  planningConfig?.postPlanSubmission?.disclaimer?.richtext
                  || '',
              }}
            />
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

PostPlanSubmission.propTypes = {
  onCreateNewPlan: PropTypes.func.isRequired,
};

export default PostPlanSubmission;
